import { FAQ_ENTRIES } from "../constants/faqContent";
export const BASE_URL = "https://www.zedipass.com";
export const DEFAULT_OG_IMAGE = `${BASE_URL}/share-preview-1200x630.jpg`;
export const OG_SITE_NAME = "ZediPass";
export const OG_LOCALE = "en_GB";
const NO_INDEX = "noindex,nofollow";

const buildWebPageSchema = ({ name, description, url }) => ({
  "@context": "https://schema.org",
  "@type": "WebPage",
  name,
  description,
  url,
});

const buildArticleSchema = ({ headline, description, url, datePublished }) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline,
  description,
  url,
  datePublished,
  author: {
    "@type": "Organization",
    name: "ZediPass Team",
  },
  publisher: {
    "@type": "Organization",
    name: "ZediPass",
    logo: {
      "@type": "ImageObject",
      url: `${BASE_URL}/ZEDI-FAVICON-BLK-512.png`,
    },
  },
});

const buildFaqSchema = (entries = []) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: entries.map((faq) => ({
    "@type": "Question",
    name: faq.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: faq.answer,
    },
  })),
});

const buildSoftwareAppSchema = ({ name, description, url }) => ({
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name,
  applicationCategory: "BusinessApplication",
  operatingSystem: "Web",
  description,
  url,
});



const LOYALTY_FAQ_ENTRIES = [
  {
    question: "What makes ZediPass different from other loyalty program software?",
    answer: "ZediPass focuses on independent retailers with instant QR sign-up, simple rewards, and real-time insights without heavy hardware or complex setup.",
  },
  {
    question: "How fast can we launch a loyalty program?",
    answer: "Most teams can set up their loyalty program in under an hour and start collecting sign-ups the same day.",
  },
  {
    question: "Do customers need to download an app?",
    answer: "Customers use the ZediPass app to show their pass and manage rewards. Your team scans their pass with the ZediPass Business app.",
  },
  {
    question: "What hardware is required?",
    answer: "None. ZediPass works with printed QR codes or simple countertop displays, and can integrate with existing point-of-sale workflows.",
  },
  {
    question: "Is ZediPass secure and GDPR compliant?",
    answer: "Yes. Customer data is encrypted in transit and at rest with GDPR-aligned controls and clear consent handling.",
  },
  {
    question: "Can we switch rewards or tiers later?",
    answer: "Absolutely. You can adjust rewards, tiers, and campaigns at any time without losing customer history.",
  },
  {
    question: "How does pricing work?",
    answer: "Pricing is designed for independent retailers with clear monthly tiers. You can start small and upgrade as you grow.",
  },
];

export const DEFAULT_SEO = {
  title: "ZediPass | Loyalty program software for independent retailers",
  description:
    "ZediPass is loyalty program software for independent retailers — instant QR sign-up, simple rewards, and real-time insights.",
  canonicalPath: "/",
  ogImage: DEFAULT_OG_IMAGE,
  robots: "index,follow",
  schema: [],
};

const ROUTE_SEO = [
  {
    pattern: /^\/$/,
    data: {
      title: "ZediPass | Loyalty program software for independent retailers",
      description:
        "ZediPass is loyalty program software for independent retailers — instant QR sign-up, simple rewards, and real-time insights.",
      canonicalPath: "/",
      schema: [
        buildWebPageSchema({
          name: "ZediPass",
          description:
            "Loyalty program software for independent retailers with instant QR sign-up, simple rewards, and real-time insights.",
          url: `${BASE_URL}/`,
        }),
      ],
    },
  },
  {
    pattern: /^\/home$/,
    data: {
      title: "ZediPass | Loyalty program software for independent retailers",
      description:
        "ZediPass is loyalty program software for independent retailers — instant QR sign-up, simple rewards, and real-time insights.",
      canonicalPath: "/",
    },
  },
  {
    pattern: /^\/product$/,
    data: {
      title: "Product | ZediPass loyalty program software",
      description:
        "Explore the loyalty program software that powers fast sign-ups, automated rewards, and customer insights for independent retailers.",
      canonicalPath: "/product/",
      schema: [
        buildSoftwareAppSchema({
          name: "ZediPass",
          description:
            "Loyalty program software for independent retailers with QR sign-up, automated rewards, and real-time insights.",
          url: `${BASE_URL}/product/`,
        }),
      ],
    },
  },
  {
    pattern: /^\/solutions$/,
    data: {
      title: "Solutions | ZediPass loyalty program software",
      description:
        "See how ZediPass loyalty program software helps independent retailers grow repeat visits with friendly, community-first experiences.",
      canonicalPath: "/solutions",
    },
  },
  {
    pattern: /^\/pricing$/,
    data: {
      title: "Pricing | ZediPass loyalty program software",
      description:
        "Simple, transparent pricing for independent retailers using ZediPass loyalty program software.",
      canonicalPath: "/pricing",
    },
  },
  {
    pattern: /^\/resources$/,
    data: {
      title: "Resources | Loyalty program insights for retailers",
      description:
        "Guides and insights for independent retailers on loyalty programs, retention, and customer engagement.",
      canonicalPath: "/resources",
    },
  },
  {
    pattern: /^\/resources\/article-content-1$/,
    data: {
      title: "Countdown to Web Summit 2025 | ZediPass",
      description:
        "An archive update from June 2025 about ZediPass and Web Summit 2025.",
      canonicalPath: "/resources/article-content-1",
      schema: [
        buildArticleSchema({
          headline: "Countdown to Web Summit 2025",
          description:
            "An archive update from June 2025 about ZediPass and Web Summit 2025.",
          url: `${BASE_URL}/resources/article-content-1`,
          datePublished: "2025-06-12",
        }),
      ],
    },
  },
  {
    pattern: /^\/resources\/article-content-2$/,
    data: {
      title: "Merchant Pilots Rolling Out | ZediPass",
      description:
        "An archive update from October 2025 about the merchant pilot programme.",
      canonicalPath: "/resources/article-content-2",
      schema: [
        buildArticleSchema({
          headline: "Merchant Pilots Rolling Out",
          description:
            "An archive update from October 2025 about the merchant pilot programme.",
          url: `${BASE_URL}/resources/article-content-2`,
          datePublished: "2025-10-08",
        }),
      ],
    },
  },
  {
    pattern: /^\/resources\/article-content-3$/,
    data: {
      title: "You Spoke, We Listened: Dynamic Menus Arrive | ZediPass",
      description:
        "An archive update from August 2025 about merchant menu tools.",
      canonicalPath: "/resources/article-content-3",
      schema: [
        buildArticleSchema({
          headline: "You Spoke, We Listened: Dynamic Menus Arrive",
          description:
            "An archive update from August 2025 about merchant menu tools.",
          url: `${BASE_URL}/resources/article-content-3`,
          datePublished: "2025-08-19",
        }),
      ],
    },
  },
  {
    pattern: /^\/faq$/,
    data: {
      title: "FAQ | ZediPass loyalty program software",
      description:
        "Answers to common questions about ZediPass loyalty program software for independent retailers.",
      canonicalPath: "/faq",
      schema: [buildFaqSchema(FAQ_ENTRIES)],
    },
  },
  {
    pattern: /^\/partners$/,
    data: {
      title: "Partners | ZediPass",
      description: "Partnership opportunities and collaboration with ZediPass.",
      canonicalPath: "/partners",
    },
  },
  {
    pattern: /^\/partners\/investors$/,
    data: {
      title: "Investors | ZediPass",
      description: "Investor information and updates from ZediPass.",
      canonicalPath: "/partners/investors",
    },
  },
  {
    pattern: /^\/partners\/investors\/platform-evolution$/,
    data: {
      title: "Platform evolution | ZediPass investors",
      description: "A strategic view of ZediPass white-label and referral platform evolution.",
      canonicalPath: "/partners/investors/platform-evolution",
    },
  },
  {
    pattern: /^\/partners\/merchant$/,
    data: {
      title: "Merchants | ZediPass",
      description: "Resources and onboarding for merchant partners.",
      canonicalPath: "/partners/merchant",
    },
  },
  {
    pattern: /^\/investors$/,
    data: {
      title: "Investors | ZediPass",
      description: "Investor information and updates from ZediPass.",
      canonicalPath: "/partners/investors",
    },
  },
  {
    pattern: /^\/merchant$/,
    data: {
      title: "Merchants | ZediPass",
      description: "Resources and onboarding for merchant partners.",
      canonicalPath: "/partners/merchant",
    },
  },
  {
    pattern: /^\/roadmap$/,
    data: {
      title: "Roadmap | ZediPass",
      description: "See what ZediPass is building next for independent retailers.",
      canonicalPath: "/roadmap",
    },
  },
  {
    pattern: /^\/findyourfit$/,
    data: {
      title: "Find your fit | ZediPass",
      description: "Find the ZediPass loyalty setup that matches your independent retail business.",
      canonicalPath: "/findyourfit",
    },
  },
  {
    pattern: /^\/business-showcase$/,
    data: {
      title: "Business showcase | ZediPass",
      description: "Explore how independent retailers use ZediPass loyalty program software.",
      canonicalPath: "/business-showcase",
    },
  },
  {
    pattern: /^\/loyalty-program$/,
    data: {
      title: "Loyalty program software for independent retailers | ZediPass",
      description:
        "Launch a loyalty program in hours with ZediPass: QR sign-up, automated rewards, and real-time insights built for independent retailers.",
      canonicalPath: "/loyalty-program",
      schema: [
        buildWebPageSchema({
          name: "Loyalty program software for independent retailers",
          description:
            "Launch a loyalty program in hours with ZediPass: QR sign-up, automated rewards, and real-time insights built for independent retailers.",
          url: `${BASE_URL}/loyalty-program`,
        }),
        buildFaqSchema(LOYALTY_FAQ_ENTRIES),
      ],
    },
  },
  {
    pattern: /^\/(enterprise|white-label-referral)$/,
    data: {
      title: "Enterprise and white label | ZediPass",
      description:
        "One branded pass across every business, partner and experience you run: for business districts, shopping destinations and hotel groups. No payment lock-in.",
      canonicalPath: "/enterprise",
    },
  },
  {
    pattern: /^\/terms$/,
    data: {
      title: "Terms of service | ZediPass",
      description: "Read the ZediPass terms of service.",
      canonicalPath: "/terms",
    },
  },
  {
    pattern: /^\/gdpr$/,
    data: {
      title: "GDPR compliance | ZediPass",
      description: "Understand how ZediPass handles GDPR-aligned data practices.",
      canonicalPath: "/gdpr",
    },
  },
  {
    pattern: /^\/privacy$/,
    data: {
      title: "Privacy policy | ZediPass",
      description: "Read the ZediPass privacy policy.",
      canonicalPath: "/privacy",
    },
  },
  {
    pattern: /^\/delete-account$/,
    data: {
      title: "Delete your ZediPass account | ZediPass",
      description:
        "Learn how to delete your ZediPass customer account and associated data, including what is removed and what may be retained.",
      canonicalPath: "/delete-account",
    },
  },
  {
    pattern: /^\/cookies$/,
    data: {
      title: "Cookie policy | ZediPass",
      description: "Read the ZediPass cookie policy.",
      canonicalPath: "/cookies",
    },
  },
  {
    pattern: /^\/referral$/,
    data: {
      title: "Referral invitation | ZediPass",
      description: "Open your ZediPass referral invitation.",
      canonicalPath: "/referral",
      robots: NO_INDEX,
    },
  },
  {
    pattern: /^\/ref\/.*$/,
    data: {
      title: "Referral invitation | ZediPass",
      description: "Open your ZediPass referral invitation.",
      canonicalPath: "/referral",
      robots: NO_INDEX,
    },
  },
  {
    pattern: /^\/our-story$/,
    data: {
      title: "Our Story | ZediPass",
      description:
        "I'm James — I built ZediPass myself. Fifteen years in design and product, now making loyalty software for independent businesses. The story behind it, and who it's for.",
      canonicalPath: "/our-story",
      ogImage: `${BASE_URL}/share-about-1200x630.jpg`,
      ogImageAlt: "James Nettey, founder of ZediPass — I built ZediPass myself",
      schema: [
        {
          "@context": "https://schema.org",
          "@type": "ProfilePage",
          mainEntity: {
            "@type": "Person",
            name: "James Nettey",
            jobTitle: "Founder",
            worksFor: { "@type": "Organization", name: "ZediPass" },
            url: `${BASE_URL}/our-story`,
            image: `${BASE_URL}/share-about-1200x630.jpg`,
            description:
              "Founder of ZediPass. Fifteen years in design and product, now building loyalty software for independent retailers.",
          },
        },
      ],
    },
  },
  {
    // Old paths now redirect to /our-story; keep them out of the index and consolidated.
    pattern: /^\/(my-story|about|about-preview)$/,
    data: {
      title: "Our Story | ZediPass",
      description: "I'm James — I built ZediPass myself.",
      canonicalPath: "/our-story",
      robots: NO_INDEX,
    },
  },
  {
    pattern: /^\/partners2$/,
    data: {
      title: "Partners preview | ZediPass",
      description: "Private partner preview page.",
      canonicalPath: "/partners2",
      robots: NO_INDEX,
    },
  },
  {
    pattern: /^\/for-shops$/,
    data: {
      title: "ZediPass for shops | Keep your customers after they leave",
      description: "Customers join your rewards with one scan. Reward every visit and stay in touch from your own phone. Join the free Founding Merchant pilot.",
      canonicalPath: "/for-shops",
    },
  },
  {
    pattern: /^\/discover$/,
    data: {
      title: "ZediPass | One pass for your local favourites",
      description: "Join local rewards with one scan. No forms at the counter and no pile of paper cards. Free on iPhone and Android.",
      canonicalPath: "/discover",
    },
  },
  { pattern: /^\/contact$/, data: { title: "Contact ZediPass | Business and enterprise enquiries", description: "Ask about ZediPass loyalty, pricing, a demo or white-label options for your business.", canonicalPath: "/contact", robots: NO_INDEX } },
  { pattern: /^\/get(?:\/(?:business|customer))?$/, data: { title: "Download ZediPass", description: "Open the ZediPass app store for your device.", canonicalPath: null, robots: NO_INDEX } },
  {
    pattern: /^\/signup\/whitelabel$/,
    data: {
      title: "WhiteLabel solutions | ZediPass",
      description: "Compare Standard, MultiBrand, and Luxury WhiteLabel options for your deployment model.",
      canonicalPath: "/signup/whitelabel",
      robots: NO_INDEX,
    },
  },
  {
    pattern: /^\/signup\/whitelabel\/standard$/,
    data: {
      title: "WhiteLabel Standard signup | ZediPass",
      description: "Provision a single-brand WhiteLabel tenant and continue to payment.",
      canonicalPath: "/signup/whitelabel/standard",
      robots: NO_INDEX,
    },
  },
  {
    pattern: /^\/signup\/whitelabel\/multibrand$/,
    data: {
      title: "WhiteLabel MultiBrand signup | ZediPass",
      description: "Provision an operator-level WhiteLabel tenant and continue to payment.",
      canonicalPath: "/signup/whitelabel/multibrand",
      robots: NO_INDEX,
    },
  },
  {
    pattern: /^\/signup\/whitelabel\/luxury$/,
    data: {
      title: "WhiteLabel Luxury signup | ZediPass",
      description: "Provision a concierge WhiteLabel tenant with manual billing review.",
      canonicalPath: "/signup/whitelabel/luxury",
      robots: NO_INDEX,
    },
  },
  {
    pattern: /^\/(signup|register|success)$/,
    data: {
      title: "Get started | ZediPass",
      description: "Create your ZediPass account or request early access.",
      canonicalPath: "/signup",
      robots: NO_INDEX,
    },
  },
  {
    pattern: /^\/(login|verify-email|forgot-password)$/,
    data: {
      title: "Account access | ZediPass",
      description: "Secure access to your ZediPass account.",
      canonicalPath: "/login",
      robots: NO_INDEX,
    },
  },
  {
    pattern: /^\/(onboardingoverview|onboardingsetup|business-setup|setup-complete)$/,
    data: {
      title: "Onboarding | ZediPass",
      description: "Complete your ZediPass setup.",
      canonicalPath: "/onboardingoverview",
      robots: NO_INDEX,
    },
  },
  {
    pattern: /^\/(payment|receipt|billing|myaccount|admin\/approve-payment)$/,
    data: {
      title: "Account settings | ZediPass",
      description: "Manage your ZediPass account settings.",
      canonicalPath: "/myaccount",
      robots: NO_INDEX,
    },
  },
  {
    pattern: /^\/(friends-family(\/.*)?|pilot-testing|utffanalytics)$/,
    data: {
      title: "Private portal | ZediPass",
      description: "Private access area.",
      canonicalPath: "/friends-family",
      robots: NO_INDEX,
    },
  },
  {
    pattern: /^\/(pilot(\/.*)?|partners\/pilot-apply)$/,
    data: {
      title: "Founding Merchant pilot | ZediPass",
      description: "Join the ZediPass pilot: limited places for eligible independent businesses, with 3 months free, then £9.99 a month for the rest of their first year.",
      canonicalPath: "/pilot",
      robots: NO_INDEX,
    },
  },
  {
    pattern: /^\/p(\/.*)?$/,
    data: {
      title: "Private Partner Preview | ZediPass",
      description: "Private partner preview page.",
      robots: NO_INDEX,
    },
  },
  {
    pattern: /^\/websummit$/,
    data: {
      title: "Web Summit | ZediPass",
      description: "Meet ZediPass at Web Summit.",
      canonicalPath: "/websummit",
      robots: NO_INDEX,
    },
  },
];

export const getSeoForPath = (pathname = "/") => {
  const rawPath = pathname || "/";
  const normalizedPath =
    rawPath !== "/" && rawPath.endsWith("/")
      ? rawPath.replace(/\/+$/, "")
      : rawPath;
  const match = ROUTE_SEO.find(({ pattern }) => pattern.test(normalizedPath));
  if (!match) {
    return {
      ...DEFAULT_SEO,
      title: "Page not found | ZediPass",
      description: "The page you requested is not available.",
      canonicalPath: normalizedPath,
      robots: NO_INDEX,
    };
  }
  return {
    ...DEFAULT_SEO,
    ...match.data,
    canonicalPath: match.data.canonicalPath ?? normalizedPath,
    schema: match.data.schema ?? [],
  };
};

export const LOYALTY_FAQ_SCHEMA_ENTRIES = LOYALTY_FAQ_ENTRIES;
